import { useState, useRef } from "react";
import MuiAppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import SaveIcon from "@mui/icons-material/Save";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import AddIcon from "@mui/icons-material/Add";
import FilterListIcon from "@mui/icons-material/FilterList";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import SettingsIcon from "@mui/icons-material/Settings";
import ViewListIcon from "@mui/icons-material/ViewList";
import GridViewIcon from "@mui/icons-material/GridView";
import { useAppStore } from "../../store";
import { ViewMode } from "../../types";
import { exportAssessment, importAssessment } from "../../utils/fileIO";
import { createAssessment, createSampleAssessment } from "../../utils/defaults";
import { MetadataDialog } from "./MetadataDialog";

export function AppBar() {
  const {
    assessment,
    viewMode,
    displayMode,
    setAssessment,
    setTitle,
    setViewMode,
    setDisplayMode,
  } = useAppStore();

  const handleDisplayModeToggle = () => {
    setDisplayMode(displayMode === "stacks" ? "grid" : "stacks");
  };

  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [titleValue, setTitleValue] = useState(assessment.title);
  const [filterAnchor, setFilterAnchor] = useState<null | HTMLElement>(null);
  const [newAnchor, setNewAnchor] = useState<null | HTMLElement>(null);
  const [isMetadataOpen, setIsMetadataOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleTitleEdit = () => {
    setTitleValue(assessment.title);
    setIsEditingTitle(true);
  };

  const handleTitleSave = () => {
    if (titleValue.trim()) {
      setTitle(titleValue.trim());
    }
    setIsEditingTitle(false);
  };

  const handleTitleCancel = () => {
    setTitleValue(assessment.title);
    setIsEditingTitle(false);
  };

  const handleTitleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleTitleSave();
    } else if (e.key === "Escape") {
      handleTitleCancel();
    }
  };

  const handleSave = () => {
    exportAssessment(assessment);
  };

  const handleLoad = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      try {
        const data = await importAssessment(file);
        setAssessment(data);
      } catch (error) {
        console.error("Failed to import file:", error);
      }
    }
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleNewBlank = () => {
    setAssessment(createAssessment("New Assessment"));
    setNewAnchor(null);
  };

  const handleNewSample = () => {
    setAssessment(createSampleAssessment());
    setNewAnchor(null);
  };

  const handleFilterChange = (mode: ViewMode) => {
    setViewMode(mode);
    setFilterAnchor(null);
  };

  return (
    <MuiAppBar position="static" color="primary" elevation={1}>
      <Toolbar>
        {/* Title */}
        <Box sx={{ display: "flex", alignItems: "center", flexGrow: 1 }}>
          {isEditingTitle ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <TextField
                value={titleValue}
                onChange={(e) => setTitleValue(e.target.value)}
                onKeyDown={handleTitleKeyDown}
                size="small"
                autoFocus
                sx={{
                  "& .MuiInputBase-input": {
                    color: "white",
                    fontSize: "1.25rem",
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "rgba(255,255,255,0.5)",
                  },
                }}
              />
              <IconButton
                size="small"
                onClick={handleTitleSave}
                color="inherit"
              >
                <CheckIcon />
              </IconButton>
              <IconButton
                size="small"
                onClick={handleTitleCancel}
                color="inherit"
              >
                <CloseIcon />
              </IconButton>
            </Box>
          ) : (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                cursor: "pointer",
                "&:hover": { opacity: 0.8 },
              }}
              onClick={handleTitleEdit}
            >
              <Typography variant="h6" component="h1" sx={{ mr: 1 }}>
                {assessment.title}
              </Typography>
              <EditIcon fontSize="small" sx={{ opacity: 0.7 }} />
            </Box>
          )}
        </Box>

        {/* Display Mode Toggle */}
        <Tooltip
          title={
            displayMode === "stacks"
              ? "Switch to Grid View"
              : "Switch to Stack View"
          }
        >
          <IconButton color="inherit" onClick={handleDisplayModeToggle}>
            {displayMode === "stacks" ? <GridViewIcon /> : <ViewListIcon />}
          </IconButton>
        </Tooltip>

        {/* Filter Button */}
        <Button
          color="inherit"
          startIcon={<FilterListIcon />}
          onClick={(e) => setFilterAnchor(e.currentTarget)}
          sx={{ mr: 1 }}
        >
          {viewMode}
        </Button>
        <Menu
          anchorEl={filterAnchor}
          open={Boolean(filterAnchor)}
          onClose={() => setFilterAnchor(null)}
        >
          <MenuItem
            selected={viewMode === "All"}
            onClick={() => handleFilterChange("All")}
          >
            All Tools
          </MenuItem>
          <MenuItem
            selected={viewMode === "Active"}
            onClick={() => handleFilterChange("Active")}
          >
            Active Only
          </MenuItem>
          <MenuItem
            selected={viewMode === "Proposed"}
            onClick={() => handleFilterChange("Proposed")}
          >
            Proposed Only
          </MenuItem>
        </Menu>

        {/* New Button */}
        <Button
          color="inherit"
          startIcon={<AddIcon />}
          onClick={(e) => setNewAnchor(e.currentTarget)}
          sx={{ mr: 1 }}
        >
          New
        </Button>
        <Menu
          anchorEl={newAnchor}
          open={Boolean(newAnchor)}
          onClose={() => setNewAnchor(null)}
        >
          <MenuItem onClick={handleNewBlank}>Blank Assessment</MenuItem>
          <MenuItem onClick={handleNewSample}>Sample Assessment</MenuItem>
        </Menu>

        {/* Load Button */}
        <Button
          color="inherit"
          startIcon={<FolderOpenIcon />}
          onClick={handleLoad}
          sx={{ mr: 1 }}
        >
          Load
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          accept=".json"
          onChange={handleFileChange}
          style={{ display: "none" }}
        />

        {/* Save Button */}
        <Button color="inherit" startIcon={<SaveIcon />} onClick={handleSave}>
          Save
        </Button>

        {/* Settings Button */}
        <Tooltip title="Assessment Details">
          <IconButton color="inherit" onClick={() => setIsMetadataOpen(true)}>
            <SettingsIcon />
          </IconButton>
        </Tooltip>
      </Toolbar>

      {/* Metadata Dialog */}
      <MetadataDialog
        open={isMetadataOpen}
        onClose={() => setIsMetadataOpen(false)}
      />
    </MuiAppBar>
  );
}
